import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import moment from "moment";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/** Giống `DashboardUpgrade/FulfilApexOverviewChart.jsx` */
function parseSlashDate(label) {
  const s = String(label).trim();
  const parts = s.split("/").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [a, b, c] = parts;
  if (a > 12) return new Date(c, b - 1, a);
  if (b > 12) return new Date(c, a - 1, b);
  return new Date(c, b - 1, a);
}

function formatCategoryLabel(label) {
  if (label == null || label === "") return "";
  const s = String(label).trim();
  if (/^\d+$/.test(s) && s.length >= 10) {
    return moment(parseInt(s, 10)).format("MM/DD");
  }
  const slash = parseSlashDate(s);
  if (slash && !Number.isNaN(slash.getTime())) {
    return moment(slash).format("MM/DD");
  }
  const date = new Date(s);
  if (!Number.isNaN(date.getTime())) return moment(date).format("MM/DD");
  if (s.length > 10) return s.substring(0, 10);
  return s;
}

function formatAxis(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n * 100) / 100);
}

function formatMoneyAxis(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(2)}k`;
  return `$${Math.round(n)}`;
}

function heightToNumber(height) {
  if (typeof height === "number" && !Number.isNaN(height) && height > 0) return height;
  if (typeof height === "string") {
    const m = height.trim().match(/^(\d+)px$/i);
    if (m) return parseInt(m[1], 10);
  }
  return 320;
}

/**
 * Ba chart độc lập (Orders bar, Revenue line, Basecost bar) — bố cục giống `FulfilApexOverviewChart` (DashboardUpgrade).
 */
function FulfilOverviewThreeCharts({
  ordersData = [],
  revenueData = [],
  basecostData = [],
  height = 320,
}) {
  const { rows, rowH } = useMemo(() => {
    const categoriesInner = ordersData.map((d) =>
      formatCategoryLabel(d.label || d.date || "")
    );
    const ordersInner = ordersData.map((d) => Number(d.value) || 0);
    const revenueInner = categoriesInner.map(
      (_, i) => Number(revenueData[i]?.value) || 0
    );
    const basecostInner = categoriesInner.map(
      (_, i) => Number(basecostData[i]?.value) || 0
    );

    const rowsInner = categoriesInner.map((name, i) => ({
      name,
      orders: ordersInner[i],
      revenue: revenueInner[i],
      basecost: basecostInner[i],
    }));

    const h = heightToNumber(height);
    const rowHInner = Math.max(170, Math.min(280, Math.round(h * 0.82)));

    return { rows: rowsInner, rowH: rowHInner };
  }, [ordersData, revenueData, basecostData, height]);

  if (!rows.length) {
    return (
      <MDBox
        sx={{
          minHeight: rowH || 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
          fontSize: "0.875rem",
        }}
      >
        Không có dữ liệu biểu đồ
      </MDBox>
    );
  }

  const chartWrap = (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={4}>
        <Card
          sx={{
            border: "1px solid #e9ecef",
            borderRadius: 2,
            boxShadow: "none",
            bgcolor: "#fafafa",
            height: "100%",
            p: 1.5,
          }}
        >
          <MDTypography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "#6c757d",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              display: "block",
              mb: 1,
            }}
          >
            Orders
          </MDTypography>
          <MDBox sx={{ width: "100%", height: rowH }}>
            <ResponsiveContainer width="100%" height={rowH}>
              <BarChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#666" }}
                  angle={-45}
                  textAnchor="end"
                  height={58}
                  interval={0}
                />
                <YAxis tick={{ fontSize: 11, fill: "#666" }} tickFormatter={formatAxis} />
                <Tooltip
                  formatter={(v) => [formatAxis(v), "Orders"]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                  }}
                />
                <Bar
                  dataKey="orders"
                  fill="#487FFF"
                  name="Orders"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Card
          sx={{
            border: "1px solid #e9ecef",
            borderRadius: 2,
            boxShadow: "none",
            bgcolor: "#fafafa",
            height: "100%",
            p: 1.5,
          }}
        >
          <MDTypography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "#6c757d",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              display: "block",
              mb: 1,
            }}
          >
            Revenue
          </MDTypography>
          <MDBox sx={{ width: "100%", height: rowH }}>
            <ResponsiveContainer width="100%" height={rowH}>
              <LineChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#666" }}
                  angle={-45}
                  textAnchor="end"
                  height={58}
                  interval={0}
                />
                <YAxis tick={{ fontSize: 11, fill: "#666" }} tickFormatter={formatMoneyAxis} />
                <Tooltip
                  formatter={(v) => [formatMoneyAxis(v), "Revenue"]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#45B369"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 4 }}
                  name="Revenue"
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Card
          sx={{
            border: "1px solid #e9ecef",
            borderRadius: 2,
            boxShadow: "none",
            bgcolor: "#fafafa",
            height: "100%",
            p: 1.5,
          }}
        >
          <MDTypography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "#6c757d",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              display: "block",
              mb: 1,
            }}
          >
            Basecost
          </MDTypography>
          <MDBox sx={{ width: "100%", height: rowH }}>
            <ResponsiveContainer width="100%" height={rowH}>
              <BarChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#666" }}
                  angle={-45}
                  textAnchor="end"
                  height={58}
                  interval={0}
                />
                <YAxis tick={{ fontSize: 11, fill: "#666" }} tickFormatter={formatMoneyAxis} />
                <Tooltip
                  formatter={(v) => [formatMoneyAxis(v), "Basecost"]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                  }}
                />
                <Bar
                  dataKey="basecost"
                  fill="#FF9F29"
                  name="Basecost"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  return <MDBox sx={{ width: "100%", overflow: "hidden" }}>{chartWrap}</MDBox>;
}

FulfilOverviewThreeCharts.propTypes = {
  ordersData: PropTypes.array,
  revenueData: PropTypes.array,
  basecostData: PropTypes.array,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default FulfilOverviewThreeCharts;
